import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import colors from '@general/colors';
import { getEventRegisteredURL, getEventRegistrationURL } from '@general/entities/event';
import getFavoriteHook from '@hooks/getFavoriteHook';
import { BodySmall, ButtonNoStyles, H4 } from '@styled';
import BaseListItem from '../BaseListItem';
import CardChip from '../CardChip';
import useIsRegisteredForEvent from '../../../../icsc-app/js/hooks/useIsRegisteredForEvent';

const TitleStyled = styled(H4)`
    transition: color 0.2s ease-in-out;

    &:hover {
        color: ${colors.primary};
    }
`;

const EventListItemRedesign = props => {
    const {
        id,
        code,
        title,
        image,
        dateRange,
        displayLocation,
        isFavorite: initialIsFavorite,
        onClick = () => {},
        favorited = () => {},
        unfavorited = () => {},
        url, // ExpressionEngine URL "attend-and-learn"
        isLoggedIn,
        showBottomLinkCheck = () => true,
        showFavoriteIcon = true,
        showShareIcon = false,
        chip = {},
        bottomLink = {},
    } = props;

    const { isRegistered: isRegisteredForEvent } = useIsRegisteredForEvent(code);
    let eventUrl = url;
    if (!url && code) {
        eventUrl = getEventRegistrationURL(code);
    }
    if (isRegisteredForEvent) {
        eventUrl = getEventRegisteredURL(code);
    }

    const useFavorite = getFavoriteHook({ type: 'event', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite, setIsFavoriteManually } = useFavorite();
    const [isRemoved, setIsRemoved] = useState(false);

    useEffect(() => {
        setIsFavoriteManually(initialIsFavorite);
    }, [initialIsFavorite, setIsFavoriteManually]);

    const onFavorite = () => {
        if (!isFavorite || isRemoved) {
            setIsRemoved(false);
            saveFavorite(id);
            favorited(id);
            return;
        }

        if (isFavorite) {
            setIsRemoved(true);
            removeFavorite(id);
            unfavorited(id);
        }
    };

    const imageBlock = (
        <>
            {image && <img alt={title} className="m-o p-0 card-img-right" src={image} />}
            {!image && <img alt="" className="m-o p-0 card-img-right" src="/assets/images/event-icon.svg" />}
        </>
    );

    const showBottomLink = showBottomLinkCheck(props);

    return (
        <BaseListItem
            id={id}
            type="event"
            title={title}
            url={eventUrl}
            isLoadingFavorite={isLoadingFavorite}
            isFavorite={isFavorite}
            onFavorite={onFavorite}
            imageBlock={imageBlock}
            isLoggedIn={isLoggedIn}
            showFavoriteIcon={showFavoriteIcon}
            showShareIcon={showShareIcon}
            contentBlock={
                <>
                    <a href={eventUrl} onClick={() => onClick(eventUrl)} onKeyDown={() => onClick(eventUrl)}>
                        <TitleStyled className="card-title d-inline-block mt-1">
                            <span className="mr-3">{title}</span>
                            {chip.color && chip.text && <CardChip color={chip.color}>{chip.text}</CardChip>}
                        </TitleStyled>
                    </a>

                    <BodySmall className="mt-0.5rem text-background">
                        {dateRange}
                        {displayLocation ? ` - ${displayLocation}` : ''}
                    </BodySmall>

                    {bottomLink.function && bottomLink.text && showBottomLink && (
                        <p className="mb-0">
                            <ButtonNoStyles
                                onClick={() => bottomLink.function(props)}
                                className="text-decoration-none font-size-sm text-primary"
                            >
                                {bottomLink.text}
                            </ButtonNoStyles>
                        </p>
                    )}
                </>
            }
        />
    );
};

EventListItemRedesign.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string.isRequired,
    dateRange: PropTypes.string,
    displayLocation: PropTypes.string,
    url: PropTypes.string,
    isFavorite: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
    favorited: PropTypes.func,
    unfavorited: PropTypes.func,
    isLoggedIn: PropTypes.bool,
    showBottomLinkCheck: PropTypes.func,
    showFavoriteIcon: PropTypes.bool,
    showShareIcon: PropTypes.bool,
    chip: PropTypes.object,
    bottomLink: PropTypes.object,
};

export default EventListItemRedesign;
